<script lang="ts">
  export type TipoComprobante = 'RECIBO' | 'FACTURA' | 'POSTERGAR';

  let {
    open = $bindable(false),
    total = 0,
    procesando = false,
    onConfirm,
  }: {
    open?: boolean;
    total: number;
    procesando?: boolean;
    onConfirm: (tipo: TipoComprobante, fel?: { nit: string; nombre: string }) => void | Promise<void>;
  } = $props();

  let seleccion = $state<TipoComprobante>('RECIBO');
  let receptorNit = $state('CF');
  let receptorNombre = $state('Consumidor Final');

  $effect(() => {
    if (open) {
      seleccion = 'RECIBO';
      receptorNit = 'CF';
      receptorNombre = 'Consumidor Final';
    }
  });

  function cerrar() {
    if (!procesando) open = false;
  }

  async function confirmar() {
    if (seleccion === 'FACTURA') {
      await onConfirm(seleccion, {
        nit: receptorNit || 'CF',
        nombre: receptorNombre || 'Consumidor Final',
      });
    } else {
      await onConfirm(seleccion);
    }
  }

  function onBackdrop(e: MouseEvent) {
    if ((e.target as HTMLElement).classList.contains('backdrop')) cerrar();
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape' && open) cerrar();
  }
</script>

<svelte:window onkeydown={onKey} />

{#if open}
  <div class="backdrop" role="presentation" onclick={onBackdrop}>
    <div class="modal" role="dialog" aria-labelledby="modal-cobro-title" aria-modal="true">
      <header>
        <h3 id="modal-cobro-title">Comprobante de cobro</h3>
        <button type="button" class="close" onclick={cerrar} disabled={procesando} aria-label="Cerrar">×</button>
      </header>

      <p class="total-line">Total a cobrar: <strong>Q{total.toFixed(2)}</strong></p>
      <p class="hint">Elija el tipo de comprobante. Recibo es la opción habitual en clínicas privadas.</p>

      <div class="opciones">
        <label class="opcion" class:sel={seleccion === 'RECIBO'}>
          <input type="radio" name="tipo-doc" value="RECIBO" bind:group={seleccion} />
          <div>
            <strong>Recibo</strong>
            <span>Comprobante interno de la clínica. No sustituye factura fiscal.</span>
          </div>
        </label>
        <label class="opcion" class:sel={seleccion === 'FACTURA'}>
          <input type="radio" name="tipo-doc" value="FACTURA" bind:group={seleccion} />
          <div>
            <strong>Factura (FEL)</strong>
            <span>Factura electrónica. Afecto a impuestos. Requiere NIT o CF.</span>
          </div>
        </label>
        <label class="opcion" class:sel={seleccion === 'POSTERGAR'}>
          <input type="radio" name="tipo-doc" value="POSTERGAR" bind:group={seleccion} />
          <div>
            <strong>Recuérdame más tarde</strong>
            <span>Guardar cobro pendiente; retomar desde la bandeja de Cobros.</span>
          </div>
        </label>
      </div>

      {#if seleccion === 'FACTURA'}
        <div class="fel-fields">
          <label>NIT <input bind:value={receptorNit} placeholder="CF" /></label>
          <label style="flex:1;min-width:12rem;">Nombre
            <input bind:value={receptorNombre} placeholder="Consumidor Final" />
          </label>
        </div>
      {/if}

      <footer>
        <button type="button" onclick={cerrar} disabled={procesando}>Cancelar</button>
        <button type="button" class="primary" onclick={confirmar} disabled={procesando}>
          {procesando ? 'Procesando…' : seleccion === 'POSTERGAR' ? 'Guardar pendiente' : 'Confirmar cobro'}
        </button>
      </footer>
    </div>
  </div>
{/if}

<style>
  .backdrop {
    position: fixed; inset: 0; z-index: 1000;
    background: rgba(0, 0, 0, 0.45);
    display: flex; align-items: center; justify-content: center;
    padding: 1rem;
  }
  .modal {
    background: #fff; border-radius: 12px; max-width: 32rem; width: 100%;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
    padding: 1.1rem 1.25rem 1rem;
  }
  header { display: flex; justify-content: space-between; align-items: center; margin-bottom: .5rem; }
  header h3 { margin: 0; font-size: 1.15rem; }
  .close { background: none; border: 0; font-size: 1.5rem; cursor: pointer; color: #666; line-height: 1; }
  .total-line { margin: .4rem 0; font-size: 1rem; }
  .hint { color: #5B7480; font-size: .88rem; margin: 0 0 .75rem; }
  .opciones { display: flex; flex-direction: column; gap: .5rem; margin-bottom: .75rem; }
  .opcion {
    display: flex; gap: .65rem; align-items: flex-start;
    padding: .65rem .75rem; border: 1px solid #d6e7ea; border-radius: 8px;
    cursor: pointer; background: #f8fcfd;
  }
  .opcion.sel { border-color: #0f766e; background: #ecfdf5; }
  .opcion input { margin-top: .2rem; }
  .opcion strong { display: block; font-size: .92rem; }
  .opcion span { display: block; font-size: .8rem; color: #5B7480; margin-top: .15rem; }
  .fel-fields { display: flex; gap: .6rem; flex-wrap: wrap; margin-bottom: .75rem; }
  .fel-fields label { display: flex; flex-direction: column; gap: .25rem; font-size: .85rem; }
  input { padding: .45rem .55rem; border: 1px solid #ccc; border-radius: 8px; font: inherit; }
  footer { display: flex; justify-content: flex-end; gap: .5rem; margin-top: .5rem; }
  footer button {
    padding: .5rem 1rem; border-radius: 8px; border: 1px solid #ccc;
    background: #fff; cursor: pointer; font: inherit;
  }
  footer button.primary {
    background: var(--aqua, #0f766e); color: #fff; border: 0;
  }
  footer button:disabled { opacity: .55; cursor: not-allowed; }
</style>
